import React, { useEffect, useState, useRef } from 'react';
import { Typography, IconButton, Input, Avatar, Button, Tooltip } from '@material-tailwind/react';
import { Logo } from "../images/Logo";
import { OrderForm } from './OrderForm';
import { OrderAnswer } from './OrderAnswer';
import { NoOrderFound } from './NoOrderFound';
import { InitialQuestions } from './InitialQuestions';
import config from '../config';

export function ChatScreen({ onClose, companyId }) {
    const [input, setInput] = useState('');
    const [messages, setMessages] = useState([]);
    const [threadId, setThreadId] = useState('');
    const [loading, setLoading] = useState(false);
    const [showOrderForm, setShowOrderForm] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        postcode: '',
        deliveryLocation: ''
    });
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 500);
    const [name, setName] = useState('');
    const [color, setColor] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [subtitle, setSubtitle] = useState('');
    const [messageColor, setMessageColor] = useState('#f7f7f7');
    const [answerColor, setAnswerColor] = useState('#e1fcff');
    const [loaderColor, setLoaderColor] = useState('#00aedb');

    useEffect(() => {
        // Laden van berichten en threadId uit lokale opslag bij het openen van het component
        const chatData = localStorage.getItem('chatBotMessages');
        if (chatData) {
            const parsedData = JSON.parse(chatData);
            setMessages(parsedData.messages);
            setThreadId(parsedData.threadId || '');
        }
    }, []);

    const scrollToBottom = () => {
        const scrollContainer = messagesContainerRef.current;
        if (scrollContainer) {
            const scrollOptions = {
                top: scrollContainer.scrollHeight,
                behavior: 'smooth'
            };
            scrollContainer.scrollTo(scrollOptions);
        }
    };

    const handleAskQuestion = () => {
        setShowOrderForm(false);
        // Optioneel: voeg een systeembericht toe dat de gebruiker een vraag kan stellen
        const systemMessage = {
            sender: 'bot',
            text: JSON.stringify([{
                json: JSON.stringify({
                    messageType: "conversation",
                    message: "Stel je vraag maar! Ik help je graag verder."
                })
            }])
        };
        setMessages(messages => [...messages, systemMessage]);
    };

    const handleRetry = () => {
        setShowOrderForm(true);
    };

    // Scroll on new messages
    useEffect(() => {
        scrollToBottom();
    }, [messages, loading, showOrderForm]);

    useEffect(() => {
        scrollToBottom();
    }, [input]);

    useEffect(() => {
        fetch(`${config.apiUrl}/chatbot-settings/${companyId}`)
            .then(response => response.json())
            .then(data => {
                setName(data.name)
                setColor(data.color)
                setMessageColor(data.messageColor)
                setAnswerColor(data.answerColor)
                setLoaderColor(data.loaderColor)
                setImageUrl(data.imageUrl)
                setSubtitle(data.subtitle)
            })
            .catch(error => {
                console.error('Error fetching settings:', error);
            });
    }, [companyId])

    function getColorBrightness(color) {
        const hex = color.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        return (r * 299 + g * 587 + b * 114) / 1000;
    }

    function getContrastColor(backgroundColor) {
        const brightness = getColorBrightness(backgroundColor);
        return brightness > 128 ? '#000000' : '#FFFFFF';
    }

    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth < 500);
        };

        window.addEventListener('resize', checkIfMobile);
        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);

    useEffect(() => {
        const chatData = { messages, threadId };
        localStorage.setItem('chatBotMessages', JSON.stringify(chatData));
    }, [messages, threadId]);

    const handleInputChange = (event) => {
        setInput(event.target.value);
    };

    const deleteConversation = () => {
        setMessages([]);
        setInput('');
        setThreadId('');
        setShowOrderForm(false)
        localStorage.removeItem('chatBotMessages');
    }

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleFormSubmit = async (e, localFormData) => {
        e.preventDefault();
        setLoading(true);
        setShowOrderForm(false); // Direct het form verbergen bij submit

        const body = JSON.stringify({
            companyId: companyId,
            thread_id: threadId,
            messages: messages,
            formType: 'whereIsOrder',
            formData: localFormData
        });

        try {
            const response = await fetch(`${config.chatbotWebhook}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: body,
            });
            const data = await response.json();

            const formMessage = {
                sender: 'user',
                text: `Order informatie:\nNaam: ${localFormData.name}\nEmail: ${localFormData.email}\nPostcode: ${localFormData.postcode}\nPlaats: ${localFormData.deliveryLocation}`
            };

            const botMessage = {
                sender: 'bot',
                text: JSON.stringify([{
                    json: JSON.stringify(data)
                }])
            };

            setMessages(messages => [...messages, formMessage, botMessage]);
        } catch (error) {
            console.error('Error submitting form:', error);
        }
        setLoading(false);
    };

    const sendMessageToBot = async (input) => {
        setLoading(true);

        let body = JSON.stringify({
            companyId: companyId,
            message: input,
            thread_id: threadId,
            messages: messages
        });

        try {
            const response = await fetch(`${config.chatbotWebhook}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: body,
            });
            const data = await response.json();
            setLoading(false);
            return data;
        } catch (error) {
            console.error('Error fetching from webhook:', error);
            return { bot: 'Er was een fout bij het ophalen van de gegevens', thread_id: threadId };
        }
    }

    const sendMessage = async () => {
        if (input.trim() === '') return;

        const userMessage = { sender: 'user', text: input };
        setMessages(messages => [...messages, userMessage]);
        setInput('');

        try {
            const responseData = await sendMessageToBot(input);
            console.log('Response van API:', responseData);

            // Check messageType direct uit de response
            if (responseData.messageType === 'whereIsOrder') {
                setShowOrderForm(true);
            } else if (responseData.messageType === 'conversation') {
                const botMessage = {
                    sender: 'bot',
                    text: JSON.stringify([{
                        json: JSON.stringify({
                            messageType: "conversation",
                            message: responseData.message
                        })
                    }])
                };
                setMessages(messages => [...messages, botMessage]);
            }

            if (responseData.thread_id) {
                setThreadId(responseData.thread_id);
            }

        } catch (error) {
            console.error('Error in sendMessage:', error);
            const botMessage = {
                sender: 'bot',
                text: JSON.stringify([{
                    json: JSON.stringify({
                        messageType: "conversation",
                        message: "Sorry, er is iets misgegaan. Probeer het later opnieuw."
                    })
                }])
            };
            setMessages(messages => [...messages, botMessage]);
        }
    };

    const handleQuestionClick = async (questionText, type) => {
        // Add user message
        const userMessage = {
            sender: 'user',
            text: questionText
        };
        setMessages(messages => [...messages, userMessage]);

        // Handle different question types
        if (type === 'whereIsOrder') {
            // Direct tonen van het orderformulier voor 'waar is mijn pakket?' vragen
            setShowOrderForm(true);
            const botMessage = {
                sender: 'bot',
                text: JSON.stringify([{
                    json: JSON.stringify({
                        messageType: "whereIsOrder",
                        message: "Vul hieronder je order gegevens in om je pakket te volgen:"
                    })
                }])
            };

        } else {
            // Send message to bot for conversation type questions
            try {
                const responseData = await sendMessageToBot(questionText);
                const botMessage = {
                    sender: 'bot',
                    text: JSON.stringify([{
                        json: JSON.stringify({
                            messageType: responseData.messageType || "conversation",
                            message: responseData.message
                        })
                    }])
                };


                // Check if the response requires showing the order form
                if (responseData.messageType === 'whereIsOrder') {
                    setShowOrderForm(true);
                } else {
                    setMessages(messages => [...messages, botMessage]);
                }
            } catch (error) {
                console.error('Error handling question click:', error);
                const errorMessage = {
                    sender: 'bot',
                    text: JSON.stringify([{
                        json: JSON.stringify({
                            messageType: "conversation",
                            message: "Sorry, er ging iets mis. Kun je het opnieuw proberen?"
                        })
                    }])
                };
                setMessages(messages => [...messages, errorMessage]);
            }
        }
    };

    return (
        <div className="flex flex-col h-full" >
            <div className="border-b-2 p-4">
                <div className='flex justify-between items-start'>
                    <div className='flex flex-col gap-2'>
                        <div className='flex gap-2 items-center'>
                            <Avatar src={imageUrl} alt="avatar" size="xs" />
                            <Typography variant='h4' className='dark:text-gray-200 text-md'>
                                {name}
                            </Typography>
                        </div>
                        <Typography variant='small' className='dark:text-gray-400'>
                            {subtitle}
                        </Typography>
                    </div>
                    <Tooltip content="Klik hier om je gesprek te verwijderen en opnieuw te beginnen.">
                        <IconButton color="blue-gray" variant='text' onClick={deleteConversation}>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                                <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                            </svg>
                        </IconButton>
                    </Tooltip>
                </div>
            </div>


            <div className="flex-1 overflow-y-auto px-4 pb-6">


                {messages.length === 0 && !showOrderForm && (
                    <div className="flex flex-col justify-end pb-4 h-full">
                        <InitialQuestions onQuestionClick={handleQuestionClick} />
                    </div>
                )}

                {messages.map((message, index) => {
                    const bgColor = message.sender === 'user' ? messageColor : answerColor;
                    const textColor = getContrastColor(bgColor);
                    const animationClass = message.sender === 'user' ? 'message-out' : 'message-in';

                    if (message.sender === 'bot') {
                        try {
                            const parsedMessage = JSON.parse(message.text);
                            const messageData = JSON.parse(parsedMessage[0].json);

                            switch (messageData.messageType) {
                                case 'orderAnswer':
                                    return <OrderAnswer key={index} data={messageData} />;
                                case 'noOrderFound':
                                    return <NoOrderFound
                                        key={index}
                                        onRetry={handleRetry}
                                        onAskQuestion={handleAskQuestion}
                                    />;
                                case 'conversation':
                                    return (
                                        <div key={index} className="w-full flex justify-start mt-2">
                                            <div className="flex items-end gap-2">
                                                <Avatar src={imageUrl} alt="avatar" size="xs" className="mb-[-15px]" />
                                                <div
                                                    className={`p-3 py-4 whitespace-pre-wrap break-words max-w-[200px] rounded-3xl rounded-bl-md ${animationClass} text-sm shadow-sm transition-all duration-200 hover:shadow-md`}
                                                    style={{
                                                        backgroundColor: bgColor,
                                                        color: textColor
                                                    }}
                                                >
                                                    {messageData.message}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                default:
                                    return (
                                        <div key={index} className="w-full flex justify-start mt-2">
                                            <div className="flex items-end gap-2">
                                                <Avatar src={imageUrl} alt="avatar" size="xs" className="" />
                                                <div
                                                    className={`p-3 py-4  whitespace-pre-wrap break-words max-w-[200px] rounded-3xl rounded-bl-md ${animationClass} text-sm shadow-sm transition-all duration-200 hover:shadow-md`}
                                                    style={{
                                                        backgroundColor: bgColor,
                                                        color: textColor
                                                    }}
                                                >
                                                    {message.text}
                                                </div>
                                            </div>
                                        </div>
                                    );
                            }
                        } catch (e) {
                            return (
                                <div key={index} className="w-full flex justify-start mt-2">
                                    <div className="flex items-end gap-2">
                                        <Avatar src={imageUrl} alt="avatar" size="xs" className="" />
                                        <div
                                            className={`p-3 py-4  whitespace-pre-wrap break-words max-w-[200px] rounded-3xl rounded-bl-md ${animationClass} text-sm shadow-sm transition-all duration-200 hover:shadow-md`}
                                            style={{
                                                backgroundColor: bgColor,
                                                color: textColor
                                            }}
                                        >
                                            {message.text}
                                        </div>
                                    </div>
                                </div>
                            );
                        }
                    }

                    // User messages
                    return (
                        <div key={index} className="w-full flex justify-end mt-2">
                            <div
                                className={`p-3 py-4 whitespace-pre-wrap break-words max-w-[200px] rounded-3xl rounded-br-md ${animationClass} text-sm shadow-sm transition-all duration-200 hover:shadow-md`}
                                style={{
                                    backgroundColor: bgColor,
                                    color: textColor
                                }}
                            >
                                {message.text}
                            </div>
                        </div>
                    );
                })}
                {showOrderForm && <OrderForm onSubmit={handleFormSubmit} loading={loading} loaderColor={loaderColor} />}
                <div ref={messagesEndRef} />

                {loading && (
                    <div className="w-full flex justify-start mt-2">
                        <div className="flex flex-col gap-1 typing-indicator">
                            <div className="flex items-center gap-2">
                                <Typography variant="small" className="text-gray-500">
                                    Aan het denken
                                </Typography>
                                <div className="loader-dots flex gap-1">
                                    <div className="loader-dot w-1.5 h-1.5 rounded-full" style={{ backgroundColor: loaderColor }}></div>
                                    <div className="loader-dot w-1.5 h-1.5 rounded-full" style={{ backgroundColor: loaderColor }}></div>
                                    <div className="loader-dot w-1.5 h-1.5 rounded-full" style={{ backgroundColor: loaderColor }}></div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

            </div>

            <div className="border-t p-2 pb-14">
                <div className="flex">
                    <input
                        type="text"
                        value={input}
                        disabled={loading || showOrderForm}
                        onChange={handleInputChange}
                        onKeyPress={event => event.key === 'Enter' ? sendMessage() : null}
                        className="flex-1 p-2 border-2 rounded-l h-10 dark:bg-black dark:border-black dark:text-gray-200"
                    />
                    <IconButton
                        onClick={sendMessage}
                        disabled={loading || showOrderForm}
                        className="rounded-l-none"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" />
                        </svg>
                    </IconButton>
                </div>
            </div>
        </div>
    );
}
